import { FC, useCallback, useState } from "react";
import { Box, Button, Paper, Typography, useMediaQuery } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { Add } from "@mui/icons-material";
import { FullWidthButton } from "src/framework/components/buttons/FullWidthButton";
import { BottomBar } from "src/framework/components/commons/BottomBar";
import { UploadInformationsCard } from "src/framework/components/commons/UploadInformations";
import { InputSwitch } from "src/framework/components/forms/inputs/InputSwitch";
import { UploadDialog } from "./Dialog";
import { Anexo } from "src/components/projetos/types";
import { formState$ } from "src/store/vendas/projetos/store";
import defaultListStore from "src/framework/store/DefaultListStore";
import { notifierStore } from "src/framework/store/NotifierStore";
import { PageTitle } from "../containers/PageTitle";
import theme from "src/theme/light";
import "./styles.css";
import { ComponentUtil } from "src/framework/commons/ComponentUtil";

interface AnexosProps {
  formState: any;
  onChangeValue: (formState: any) => void
  errorState: string[];
  onChangeError: (errorState: any) => void
  standAlone?: boolean;
  readOnly?: boolean;
  finishProjectButtonIsDisabled?: boolean;
  onClickNext: () => void
  onClickBack: () => void
}

const useStyles = makeStyles({
  uploadButton: {
    height: 42
  },
  codigoVendaText: {
    color: '#CC8F00'
  }
})

const Anexos: FC<AnexosProps> = ({ onChangeValue, finishProjectButtonIsDisabled, onClickBack, onClickNext, standAlone, readOnly, formState }) => {
  const classes = useStyles();

  const [anexosUpload, setAnexosUpload] = useState<Anexo[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const FooterButton = isMobile ? Box : BottomBar;
  
  const validate = useCallback(()=>{
    const state = { ...formState }

    const haveUnidadeConsumidora = state?.solicitacao?.unidade_consumidora || false
    const anexos = state?.anexos || []

    if (haveUnidadeConsumidora && anexos.length === 0) {
      notifierStore.setNotifier({
        title: "Aviso!!!",
        content: "Quando o projeto possui unidade consumidora é obrigatório anexar uma fatura.",
        onClose: () => {
            notifierStore.setOpen(false);
        },
        timeOut: 2500
      });
      notifierStore.setOpen(true);
      return false
    } 

    return true;
  }, [formState])

  return (
    <>
      <Paper elevation={0} className="verticais-anexos">
        <PageTitle title="Anexos" codigoVenda={formState?.codigo_venda} />
        <Box p={2}>
          <Box className="X12" py={isMobile ? 0 : 2}>
            <Typography variant="h6" align="left" className="subtitulo-anexos"><strong>Documentos vinculados ao projeto:</strong></Typography>
          </Box>

          <Box className="X12 row">
            <Box flex={isMobile ? 'unset' : 1}>
              <Typography align='left' variant="body1" color="textSecondary" className="label-switch">O seu cliente possui unidade consumidora?</Typography>
              <div className="X12 M6 L3" style={{ display: "flex", alignContent: "flex-start", paddingTop: "4px" }}>
                <InputSwitch
                  margin="none"
                  label={!!formState?.solicitacao?.unidade_consumidora ? 'Sim' : 'Não'}
                  value={formState?.solicitacao?.unidade_consumidora || false}
                  onChangeValue={(value) => {
                      const state: any = { ...formState }
                      state.solicitacao = { ...state.solicitacao, unidade_consumidora: value }
                      onChangeValue(state)
                  }}
                  style={{ marginTop: "8px" }}
                />
              </div>
            </Box>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              className={classes.uploadButton}
              onClick={() => setOpenDialog(true)}
              fullWidth={isMobile}
              style={isMobile ? {margin: "25px 0px 15px 0px"} : {}}
            >
              Adicionar Anexo
            </Button>
          </Box>

        {formState?.anexos?.length > 0 && (
          <Box py={2}>
            <Typography variant="h6" align="left" className="subtitulo-anexos"><strong>Documentos carregados neste projeto:</strong></Typography>
          </Box>
        )}

        {formState?.anexos?.map((anexo: any, index: number) => {
          return (
            <Box mb={2}>
              <UploadInformationsCard
                onPreview={url => {
                const currentUrl = `https://${defaultListStore.uploadUrl}${url}`
                window.open(currentUrl, '_blank')
              }}
              data={{
                url: anexo.url,
                description: anexo.descricao,
                filename: anexo.nome,
              }}
              id={anexo.url}
              onDelete={(url) => {
                const itemToRemove = formState?.anexos?.find((item: any) => item.url === url)

                if (!itemToRemove?.created_at) {
                  const state: any = { ...formState }
                  const newAnexos = state?.anexos?.filter((item: any) => item.url !== url)
                  state.anexos = newAnexos
                  onChangeValue(state)
                  setAnexosUpload(newAnexos)
                } else {
                  const state: any = { ...formState }
                  const newAnexos = state?.anexos?.map((item: any) => {
                    if (item.url === url) {
                      item["@remove"] = true
                      return item
                    }
                    return item
                  })
                  state.anexos = newAnexos
                  setAnexosUpload(newAnexos)
                  onChangeValue(state)
                }
              }}
              />
            </Box>
          )
        })}
        </Box>
      </Paper>
      <FooterButton style={isMobile ? { marginTop: 10, padding: 15 } : {}}>
        {standAlone ? "" : (
          <FullWidthButton
            text={!readOnly ? "Voltar" : "Fechar"}
            color={!readOnly ? "secondary" : "primary"}
            onClick={() => {
              formState$.next({...formState$.value, ...formState})
              onClickBack()
            }}
            variant='outlined'
            style={isMobile ? { marginTop: 15 } : {}}
          />
        )}
        {!readOnly &&
          <FullWidthButton
            disableElevation={true}
            text={"Salvar Projeto"}
            color={"primary"}
            disabled={finishProjectButtonIsDisabled}
            variant={"contained"}
            onClick={() => {
              if (!validate()) return;
              onClickNext()
            }}
            style={isMobile ? { marginTop: 15, marginBottom: 15 } : {}}
          />
        }
      </FooterButton>
      <UploadDialog
        onCapturaCodigoVenda={(codigoVenda) => {
          const state: any = { ...formState }
          state.codigo_venda = codigoVenda
          onChangeValue(state)
        }}
        onChange={(uploads: any) => {
          const anexosOld = formState?.anexos || []
          const r = ComponentUtil.getListPersist(anexosOld, [...(anexosUpload || []), ...uploads], "url")
          const _anexos = [...r.data, ...r.removedData.map((rd: Anexo) => {
            rd["@remove"] = true
            return rd;
          })]
          setAnexosUpload(_anexos)
          const state: any = { ...formState }
          state.anexos = _anexos;
          onChangeValue(state)
        }}
        formState={formState}
        close={() => setOpenDialog(false)}
        openDialog={openDialog}
      />
    </>
  );
}

export { Anexos }